import React, { useEffect, useState } from 'react';
import { customersService, Customer } from '../services/customersService';
import { Badge } from '../components/ui/badge';

interface CustomerReferenceFieldProps {
  customerId?: string | number;
  showPoints?: boolean;
  showZalo?: boolean;
  sourcePlatform?: string; // 'grab' | 'shopee' | 'xanhsm' | etc.
}

const CustomerReferenceField: React.FC<CustomerReferenceFieldProps> = ({
  customerId,
  showPoints = false,
  showZalo = false,
  sourcePlatform,
}) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) {
      setCustomer(null);
      return;
    }

    const fetchCustomer = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const customerData = await customersService.getCustomer(
          String(customerId)
        );
        setCustomer(customerData);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load customer'
        );
        setCustomer(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId]);

  if (!customerId) {
    return null;
  }

  if (isLoading) {
    return <span className="text-muted-foreground text-sm">Loading...</span>;
  }

  if (error || !customer) {
    return (
      <span className="text-muted-foreground text-sm italic">
        Unregistered user
      </span>
    );
  }

  // Get customer display name
  const getCustomerDisplayName = (customer: Customer): string => {
    // Handle customers with firstName/lastName
    const fullName = `${customer.firstName || ''} ${
      customer.lastName || ''
    }`.trim();

    if (fullName) {
      return fullName;
    }

    // Handle Zalo customers that might have 'name' instead of firstName/lastName
    if ((customer as any).name) {
      return (customer as any).name;
    }

    // Fallback to email
    return customer.email || 'Unknown Customer';
  };

  const displayName = getCustomerDisplayName(customer);

  // Map platform names to display labels
  const getPlatformLabel = (platform?: string): string | null => {
    if (!platform) return null;
    const platformMap: Record<string, string> = {
      grab: 'Grab',
      shopee: 'Shopee',
      xanhsm: 'Xanh SM',
      xanh_sm: 'Xanh SM',
    };
    return platformMap[platform.toLowerCase()] || platform;
  };

  const platformLabel = getPlatformLabel(sourcePlatform);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-medium text-sm">
        <span data-sensitive-field="firstName">{displayName}</span>
      </span>
      {customer.phone && (
        <span className="text-xs text-muted-foreground">
          <span data-sensitive-field="phone">{customer.phone}</span>
        </span>
      )}
      {showPoints && customer.totalSpent !== undefined && (
        <span className="text-xs text-muted-foreground">
          (
          <span data-sensitive-field="amount">
            {customer.totalSpent.toLocaleString()}₫
          </span>
          )
        </span>
      )}
      {platformLabel && (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200 text-xs"
        >
          {platformLabel}
        </Badge>
      )}
    </div>
  );
};

export default CustomerReferenceField;
